import React, { useState } from 'react';
import './BillingAddressModal.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export interface BillingAddress {
  fullName: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

const COUNTRY_CODES = [
  'IN', 'US', 'GB', 'CA', 'AU', 'AE', 'SG', 'DE', 'FR', 'NL', 'ES', 'IT', 'IE', 'SE', 'CH', 'JP',
  'KR', 'CN', 'HK', 'ID', 'MY', 'TH', 'PH', 'VN', 'PK', 'BD', 'LK', 'NP', 'SA', 'QA', 'KW', 'OM',
  'ZA', 'NG', 'KE', 'EG', 'BR', 'MX', 'AR', 'NZ',
];

const countryName = (code: string): string => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code;
  } catch {
    return code;
  }
};

const EMPTY: BillingAddress = { fullName: '', line1: '', line2: '', city: '', state: '', postalCode: '', country: 'IN' };

interface Props {
  initial?: BillingAddress | null;
  onSaved: (address: BillingAddress) => void;
  onClose: () => void;
}

/** Invoice address, shown when a paid plan is chosen. No tax fields. */
const BillingAddressModal: React.FC<Props> = ({ initial, onSaved, onClose }) => {
  const [form, setForm] = useState<BillingAddress>(initial ?? EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (key: keyof BillingAddress) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/me/billing-address`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not save the address.');
        return;
      }
      onSaved(data.address as BillingAddress);
    } catch {
      setError('Could not reach the server. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const complete = form.fullName.trim() && form.line1.trim() && form.city.trim() && form.state.trim() && form.postalCode.trim();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal-container ba-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit} noValidate>
        <div className="modal-header">
          <h2>Billing address</h2>
          <button type="button" className="modal-close" aria-label="Close" onClick={onClose}>✕</button>
        </div>
        <p className="ba-note">We need this for your invoice before you start a paid plan.</p>

        <div className="modal-field">
          <label htmlFor="ba-name">Full name</label>
          <input id="ba-name" type="text" autoComplete="name" value={form.fullName} onChange={set('fullName')} />
        </div>
        <div className="modal-field">
          <label htmlFor="ba-line1">Address line 1</label>
          <input id="ba-line1" type="text" autoComplete="address-line1" value={form.line1} onChange={set('line1')} />
        </div>
        <div className="modal-field">
          <label htmlFor="ba-line2">Address line 2 (optional)</label>
          <input id="ba-line2" type="text" autoComplete="address-line2" value={form.line2} onChange={set('line2')} />
        </div>
        <div className="ba-grid">
          <div className="modal-field">
            <label htmlFor="ba-city">City</label>
            <input id="ba-city" type="text" autoComplete="address-level2" value={form.city} onChange={set('city')} />
          </div>
          <div className="modal-field">
            <label htmlFor="ba-state">State</label>
            <input id="ba-state" type="text" autoComplete="address-level1" value={form.state} onChange={set('state')} />
          </div>
          <div className="modal-field">
            <label htmlFor="ba-postal">Postal code</label>
            <input id="ba-postal" type="text" autoComplete="postal-code" value={form.postalCode} onChange={set('postalCode')} />
          </div>
          <div className="modal-field">
            <label htmlFor="ba-country">Country</label>
            <select id="ba-country" autoComplete="country" value={form.country} onChange={set('country')}>
              {COUNTRY_CODES.map((c) => <option key={c} value={c}>{countryName(c)}</option>)}
            </select>
          </div>
        </div>

        {error && <div className="ba-error" role="alert">{error}</div>}

        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={saving || !complete}>{saving ? 'Saving…' : 'Save and continue'}</button>
        </div>
      </form>
    </div>
  );
};

export default BillingAddressModal;
